import React, { useState } from 'react';
import {
  useMaterialReactTable,
  MaterialReactTable
} from 'material-react-table';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Typography,
  Link,
  Grid,
  FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  addTargetMetric,
  updateTargetMetric,
  deleteTargetMetric
} from './data';
import { textAlign } from '@mui/system';

export default function TargetMetricsTable({
  targetFacetId,
  initialMetrics,
  onMetricsChange,
  parentFacetActive
}) {
  const sanitizeMetrics = (items = []) => {
    return (items ?? []).filter((metric) => metric && typeof metric === 'object');
  };

  const [metrics, setMetrics] = useState(sanitizeMetrics(initialMetrics));
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [formValues, setFormValues] = useState({
    targetFacetId,
    name: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // State for delete confirmation
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState(null);

  const handleOpenAdd = () => {
    setEditingMetric(null);
    setFormValues({
      targetFacetId,
      name: '',
      active: true
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (metric) => {
    setEditingMetric(metric);
    setFormValues(metric);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingMetric) {
        const resp = await updateTargetMetric(formValues);
        if (resp.status === 'success' && resp.instance) {
          const updatedMetrics = sanitizeMetrics(metrics).map(m =>
            m.id === resp.instance.id ? resp.instance : m
          );
          setMetrics(updatedMetrics);
          onMetricsChange(updatedMetrics);
          setOpenDialog(false);
        } else if (resp.status === 'success') {
          setError('Unable to save metric: response did not include saved metric data.');
        } else {
          setError(resp.error);
        }
      } else {
        const resp = await addTargetMetric(formValues);
        if (resp.status === 'success' && resp.instance) {
          const updatedMetrics = [...sanitizeMetrics(metrics), resp.instance];
          setMetrics(updatedMetrics);
          onMetricsChange(updatedMetrics);
          setOpenDialog(false);
        } else if (resp.status === 'success') {
          setError('Unable to add metric: response did not include saved metric data.');
        } else {
          setError(resp.error);
        }
      }
    } catch (e) {
      setError(e.response?.data ?? e.message)
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (metric) => {
    setMetricToDelete(metric);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!metricToDelete) return;
    setSaving(true);
    setError('');
    try {
      const resp = await deleteTargetMetric(metricToDelete.id, targetFacetId);
      if (resp.status === 'success') {
        const updatedMetrics = sanitizeMetrics(metrics).filter(m => m.id !== metricToDelete.id);
        setMetrics(updatedMetrics);
        onMetricsChange(updatedMetrics);
      } else setError(resp.error);
    } catch (e) {
      setError(e.response?.data ?? e.message)
    } finally {
      setSaving(false);
      setConfirmDeleteOpen(false);
      setMetricToDelete(null);
    }
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      grow: false,
      size: 200,
      Cell: ({ row }) =>
        parentFacetActive ? (
          <Link onClick={() => handleOpenEdit(row.original)}>
            <Typography variant="body2">{row.original.name}</Typography>
          </Link>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: 'text.disabled', fontStyle: 'italic' }}
          >
            {row.original.name}
          </Typography>
        )
    },
    {
      accessorKey: 'description',
      header: 'Description',
      grow: true,
      size: 300,
      Cell: ({ row }) =>
        (
          <Typography variant="body2" whiteSpace={'pre-wrap'}>
            {row.original.description}
          </Typography>
        )
    },
    {
      accessorKey: 'active',
      header: 'Active',
      grow: false,
      size: 50,
      Cell: ({ cell }) => <>{cell.getValue() ? 'True' : 'False'}</>
    },
    {
      id: 'actions',
      header: '',
      size: 50,
      Cell: ({ row }) =>
        parentFacetActive && (
          <IconButton
            size="small"
            color="error"
            onClick={() => confirmDelete(row.original)}
            disabled={saving}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      enableSorting: false,
      enableColumnFilter: false
    }
  ];

  const sortedMetrics = sanitizeMetrics(metrics).sort((a, b) => {
    if (a.active && !b.active) return -1;
    if (!a.active && b.active) return 1;
    return (a.name ?? '').localeCompare((b.name ?? ''), undefined, { sensitivity: 'base' });
  });

  const table = useMaterialReactTable({
    layoutMode: 'grid',
    columns,
    data: sortedMetrics,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    enableRowActions: false,
    enablePagination: false,
    muiTablePaperProps: { elevation: 0, variant: 'outlined' },
    muiTableBodyCellProps: {
      sx: { padding: '2px 4px', fontSize: '0.875rem' }
    },
    muiTableBodyRowProps: {
      sx: { height: '24px' }
    },
    muiTableHeadCellProps: {
      sx: {
        padding: '4px 8px',
        fontSize: '0.8rem',
        backgroundColor: '#f5f5f5'
      }
    }
  });

  return (
    <Grid container direction='row-reverse'>
      <Grid item xs={2} sx={{textAlign: 'center'}}>
        {parentFacetActive && (
          <Button onClick={handleOpenAdd} disabled={saving} size="small" variant="contained">
            Add Metric
          </Button>
        )}
      </Grid>
      <Grid item xs={10}>
        {error && <Alert severity="error">{error}</Alert>}
        <MaterialReactTable table={table} />
        {/* Add/Edit Metric Dialog */}
        <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>
            {editingMetric ? 'Edit Metric' : 'Add Metric'}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" alignItems="center" columnGap={1}>
              <Typography variant="body" sx={{ flexShrink: 0, width: '100px' }}>Name:</Typography>
              <TextField
                value={formValues.name}
                size="small"
                fullWidth
                inputProps={{ maxLength: 30 }}
                onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
              />
              <FormHelperText>{(formValues.name || '').length}/30</FormHelperText>
            </Stack>
            <Stack direction="row" alignItems="center" columnGap={1}>
              <Typography variant="body" sx={{ flexShrink: 0, width: '100px' }}>Description:</Typography>
              <TextField
                value={formValues.description}
                multiline={true}
                rows={2}
                inputProps={{ maxLength: 100 }}
                size="small"
                fullWidth
                onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
              />
              <FormHelperText>{(formValues.description || '').length}/100</FormHelperText>
            </Stack>
            <FormControlLabel
              control={<Checkbox size="small" checked={formValues.active} onChange={(e) => setFormValues({ ...formValues, active: e.target.checked })} />}
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={saving} size="small">Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving} size="small">
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete metric{' '}
              <strong>{metricToDelete?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirmed}
              size="small"
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
}